import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { useSelector } from 'react-redux';
import {
  cancelOrderManually, completedOrderManually,
  deliverOrderManually, getOrderHistory,
  getOrdersFromShipment,
  notDeliverOrderManually
} from 'store/requests/order';
import { getStores } from '../../../../store/requests/definitions';
import {
  Tooltip,
  Col,
  Button,
  Row,
  ConfigProvider,
  Table,
  Tag,
  Form,
  Dropdown,
  notification,
  Menu,
  Modal, Spin
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  debouncedDataLoadWaitMs,
  ORDER_STATUS_COLORS,
  ORDER_STATUSES,
  ORDER_TYPES,
  COURIER_CHANGE_STATES,
  ITEM_TYPE_ORDERS,
  COURIER_SOURCE
} from 'helpers/constants';
import {
  DownloadOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  RetweetOutlined,
  SwapOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import ChangeOrderCourier from '../../../../components/Orders/ChangeOrderCourier';
import { getCourier } from 'store/requests/courier';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import ExportOrderData from '../../../../components/Orders/ExportOrderData';
import OrderTransfer from '../../../../components/Orders/OrderTransfer';

let lastQuery = false;
const Edit = React.lazy(() => import('../../../../components/Orders/Edit'));
const OrdersTableFilters = React.lazy(() => import('./OrdersTableFilters'));

const Orders = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled, changeCourier, changeStatus, edit, orderTransfer } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [couriersOfZone, setCouriersOfZone] = useState(null);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangeCourierModalOpen, setIsChangeCourierModalOpen] =
    useState(false);
  const [selectedStoreName, setSelectedStoreName] = useState('');
  const [openDateModal, setOpenDateModal] = useState(false);
  const [orderNumberForTransfer, setOrderNumberForTransfer] = useState(null);
  const [orderHistoryStatus, setOrderHistoryStatus] = useState({
    DELIVERED: false,
    NOT_DELIVERED: false,
  });
  const [getOrderHistoryLoading, setGetOrderHistoryLoading] = useState(false);
  const [openOrderTransferModal, setOpenOrderTransferModal] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });
  const activeStoreId = useSelector(
    (state) => state.auth?.user.activeStore?.id,
  );
  const activeRegionId = useSelector(
    (state) => state.auth?.user.activeRegion?.id,
  );

  const orderTypeMap = {
    [ITEM_TYPE_ORDERS.regularOrder]: t('regularOrder'),
    [ITEM_TYPE_ORDERS.changeOrder]: t('changeOrder'),
    [ITEM_TYPE_ORDERS.returnPickup]: t('returnPickup'),
  };

  const courierSourceMap = {
    [COURIER_SOURCE.Bringo]: t('Bringo'),
    [COURIER_SOURCE.CepteŞOK]: t('CepteŞOK'),
  };

  useEffect(() => {
    if (activeRegionId) {
      setLoading(true);
      getStores(
        `filter=region.id==${activeRegionId} and active==true&size=2000`,
      ).finally(() => setLoading(false));
    }
  }, [activeRegionId]);

  const changeOrderCourier = (orderZone, orderStatus) => {
    if (!COURIER_CHANGE_STATES.includes(orderStatus)) {
      notification.warn({
        message: t('warning'),
        description: t('statusOfTheOrderIsNotSuitableForChangingTheCourier'),
      });
      return;
    }

    setIsChangeCourierModalOpen(true);

    if (orderZone) {
      let queryString = `filter=user.roles.active==true and user.roles.zone.id==${orderZone} and active==true`;
      setLoading(true);
      getCourier(queryString)
        .then((courier) => {
          const { _embedded, page } = courier;
          const { courierResponseDtoList } = _embedded ? _embedded : [];
          setCouriersOfZone(
            courierResponseDtoList ? courierResponseDtoList : [],
          );
        })
        .finally(() => setLoading(false));
    }
  };

  const orderTransferFn = (orderNumber) => {
    setOpenOrderTransferModal(true);
    setOrderNumberForTransfer(orderNumber);
  };

  const { confirm } = Modal;
  const showConfirmForOrderChangeStatus = (id, status) => {
    const statusTranslate = t(status);
    const confirmationMessage = t('doYouConfirmChangeOrderStatus', {
      statusTranslate,
    });

    const actionMap = {
      [ORDER_STATUSES.CANCELLED]: cancelOrderManually,
      [ORDER_STATUSES.DELIVERED]: deliverOrderManually,
      [ORDER_STATUSES.NOT_DELIVERED]: notDeliverOrderManually,
      [ORDER_STATUSES.COMPLETED]: completedOrderManually,
    };

    const action = actionMap[status];

    confirm({
      title: t('doYouConfirm'),
      icon: <ExclamationCircleOutlined />,
      content: confirmationMessage,
      okText: t('ok'),
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        action(id, status).then((res) => {
          if (res) {
            notificationChangeStatus('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          }
        });
      },
      onCancel() {},
    });
  };

  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('orderStatusChanged'),
    });
  };

  const columns = [
    {
      dataIndex: 'customerOrderNumber',
      title: t('orderId'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'partner',
      title: t('orderChannel'),
      sorter: true,
      showSorterTooltip: false,
      render: (partner, items) => {
        const orderChannel = {
          POSITIVE: 'ŞOK-Positive',
          TRANSFORMATION: 'ŞOK-Yeni Ecom',
        }
        return orderChannel[items.channel] ?? partner;
      }
    },
    {
      dataIndex: 'orderStatus',
      title: t('shipmentStatus'),
      width: 180,
      sorter: true,
      showSorterTooltip: false,
      render: (status) => (
        <Tag color={ORDER_STATUS_COLORS[status]}>{t(status)}</Tag>
      ),
    },
    {
      dataIndex: 'timeSlot',
      title: t('slotTime'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'address',
      title: t('address'),
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (address) => address.text ?? '-',
    },
    {
      dataIndex: 'externalCreateTime',
      title: t('orderCreatedTime'),
      sorter: true,
      showSorterTooltip: false,
      render: (time) => time.replace('T', ' '),
    },
    {
      dataIndex: 'deliveryDate',
      title: t('deliveryDate'),
      sorter: true,
      showSorterTooltip: false,
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'slot',
      title: t('store'),
      sorter: true,
      showSorterTooltip: false,
      render: (slot) =>
        `${slot?.zone?.store?.code} - ${slot?.zone?.store?.name}` ?? '-',
    },
    {
      dataIndex: 'courierSource',
      title: t('courierSource'),
      sorter: true,
      showSorterTooltip: false,
      render: (source) => courierSourceMap[source] || '-',
    },
    {
      dataIndex: 'orderType',
      title: t('shipmentType'),
      sorter: true,
      showSorterTooltip: false,
      render: (slot) => orderTypeMap[slot] || '-',
    },
    {
      dataIndex: 'additional',
      title: t('ADDITIONAL_ORDER'),
      width: 70,
      sorter: true,
      showSorterTooltip: false,
      render: (additional) => additional ? t('additionalExists') : t('additionalNotExists'),
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            overlay={
              <Menu>
                {edit && <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsEditModalOpen(true);
                  }}
                >
                  {t('goToDetail')}
                </Menu.Item>}
                {changeCourier && <Menu.Item
                  key="changeCourier"
                  icon={<SwapOutlined />}
                  onClick={() => {
                    changeOrderCourier(
                      dataItem.slot.zone.id,
                      dataItem.orderStatus
                    );
                  }}
                >
                  {t('changeCourier')}
                </Menu.Item>}
                {changeStatus && (
                  <Menu.SubMenu
                    key="changeStatus"
                    disabled={[
                      ORDER_STATUSES.CANCELLED,
                      ORDER_STATUSES.COMPLETED,
                      ORDER_STATUSES.COLLECTING,
                    ].includes(dataItem.orderStatus)}
                    title={
                      <span>
                        <RetweetOutlined /> {t('changeStatus')}
                      </span>
                    }
                  >
                    {showDropdown(dataItem)}
                  </Menu.SubMenu>
                )}
                {orderTransfer && (
                    <Menu.Item
                      key="orderTransfer"
                      icon={<RollbackOutlined />}
                      disabled={
                        dataItem.orderStatus !== ORDER_STATUSES.NEW_ORDER
                      }
                      onClick={() => {
                        orderTransferFn(dataItem.orderNumber);
                      }}
                    >
                      {t('orderTransfer')}
                    </Menu.Item>
                  )}
              </Menu>
            }
            placement="bottomRight"
            trigger={['click']}
            arrow
            onClick={() => handleDropdownClick(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
        ),
    },
  ];

  const handleDropdownClick = (dataItem) => {
    setSelectedItemId(dataItem.id);
    setOrderHistoryStatus({ DELIVERED: false, NOT_DELIVERED: false });

    const validStatuses = [ORDER_STATUSES.ARRIVED_AT_STORE, ORDER_STATUSES.WAITING_FOR_APPROVAL];

    if (validStatuses.includes(dataItem.orderStatus)) {
      setGetOrderHistoryLoading(true);

      getOrderHistory(dataItem.id)
        .then((res) => {
          const { orderHistoryDtoList } = res._embedded;

          const orderStatus = orderHistoryDtoList.filter(({ status }) =>
            [ORDER_STATUSES.DELIVERED, ORDER_STATUSES.NOT_DELIVERED].includes(status)
          );

          const deliveredStatus = orderStatus.some(item => item.status === ORDER_STATUSES.DELIVERED);
          const notDeliveredStatus = orderStatus.some(item => item.status === ORDER_STATUSES.NOT_DELIVERED);

          setOrderHistoryStatus({
            DELIVERED: deliveredStatus,
            NOT_DELIVERED: notDeliveredStatus,
          });
        })
        .catch((err) => {
          console.error("Error fetching order history:", err);
        })
        .finally(() => {
          setGetOrderHistoryLoading(false);
        });
    }
  };


  const showDropdown = (dataItem) => {
    if(getOrderHistoryLoading) return <Menu.Item key="loading" ><Spin size="small" /></Menu.Item>
    if(dataItem.orderStatus === ORDER_STATUSES.NOT_DELIVERED || orderHistoryStatus.NOT_DELIVERED){
      return  <>
        {createMenuItems(
          dataItem,
          ORDER_STATUSES.CANCELLED,
          'CANCELLED',
        )}
      </>
    } else if (orderHistoryStatus.DELIVERED || dataItem.orderStatus === ORDER_STATUSES.DELIVERED) {
      return <>
        { createMenuItems(
          dataItem,
          ORDER_STATUSES.COMPLETED,
          'COMPLETED',
        )}
      </>
    }else {
      return <>
        {createMenuItems(
          dataItem,
          ORDER_STATUSES.CANCELLED,
          'CANCELLED',
        )}
        {createMenuItems(
          dataItem,
          ORDER_STATUSES.DELIVERED,
          'DELIVERED',
        )}
        {createMenuItems(
          dataItem,
          ORDER_STATUSES.NOT_DELIVERED,
          'NOT_DELIVERED',
        )}
      </>
    }
  }

  const createMenuItems = (dataItem, statusKey, statusText) => (
    <Menu.Item
      key={statusKey}
      onClick={() => {
        showConfirmForOrderChangeStatus(dataItem.id, statusKey);
      }}
    >
      {t(statusText)}
    </Menu.Item>
  );

  const orderStatusSelectOptions = Object.keys(ORDER_STATUSES).map((status) => {
    return { value: ORDER_STATUSES[status], label: t(status) };
  });

  const getQueryString = () => {
    let defaultDateSort = true;
    let queryString = null;

    if (filters.country) {
      queryString = `filter=slot.zone.store.region.country.id==1`;
    } else {
      queryString = activeStoreId
        ? `filter=slot.zone.store.id==${activeStoreId}`
        : `filter=slot.zone.store.region.id==${activeRegionId}`;
    }

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item] && item !== 'country') {
          const partnerMappings = {
            'ISTEGELSIN': ` and ${item}==${filters[item]}`,
            'SOK_POSITIVE': ` and ${item}==SOK and channel==POSITIVE`,
            'SOK_ECOM': ` and ${item}==SOK and channel==TRANSFORMATION`,
          };

          queryString += partnerMappings[filters[item]] || '';

          if (item !== 'partner') {
            queryString += ` and ${item === 'orderStatus' ? 'status' : item}${item === 'externalCreateTime=ge' || item === 'externalCreateTime=le' ? '=' : '=='}${item === 'address.text' ? ` ${filters[item]}*` : filters[item]}`;
          }
        }
      });
    }
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex === 'orderStatus' ? 'status' : tableParams.column.dataIndex === 'slot' ? 'slot.zone.store.code' : tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
      defaultDateSort = false;
    }

    if (defaultDateSort) {
      queryString = `${queryString}&sort=deliveryDate,desc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (queryString !== lastQuery && !loading) {
      lastQuery = queryString;
      setLoading(true);
      getOrdersFromShipment(queryString)
        .then((res) => {
          const { _embedded, page } = res;
          setRows(_embedded ? _embedded?.orderResponseDtoList : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }
  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };
  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  const renderActionButtons = () => {
    return (
      <Tooltip title={t('download')}>
        <Button
          style={{ marginRight: '3px' }}
          shape="round"
          icon={<DownloadOutlined />}
          onClick={() => setOpenDateModal(true)}
        ></Button>
      </Tooltip>
    );
  };

  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      customerOrderNumber: 'orderId',
      orderStatus: 'shipmentStatus',
      'address.neighborhood': 'neighborhood',
      'address.text': 'address',
      'externalCreateTime=ge': 'externalCreateTimeGe',
      'externalCreateTime=le': 'externalCreateTimeLe',
      deliveryDate: 'deliveryDate',
      timeSlot: 'slotTime',
      'slot.zone.store.id': 'store',
      country: 'acrossTurkey',
      partner: 'orderChannel',
      courierSource: 'courierSources',
      orderType: 'shipmentType',
      additional: 'ADDITIONAL_ORDER',
    };

    const formatters = {
      timeSlot: value => value,
      'externalCreateTime=ge': value => value.split('T')[0],
      'externalCreateTime=le': value => value.split('T')[0],
      'slot.zone.store.id': () => selectedStoreName,
      courierSource: value => (value === COURIER_SOURCE.Bringo ? t('Bringo') : t('CepteŞOK')),
      orderType: value => orderTypeMap[value] || value,
    };

    return Object.keys(filters).map((item, idx) => {
      const filterValue = filters[item];
      if (!filterValue) return null;

      const localizedLabel = t(fieldLocalizationVars[item]);
      const formatter = formatters[item] || ((value) => t(value));

      return (
        <Tag
          key={idx}
          closable
          color="geekblue"
          onClose={(e) => {
            e.preventDefault();
            removeItemFromFilters(item);
          }}
        >
          {`${localizedLabel}: ${formatter(filterValue)}`}
        </Tag>
      );
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      {openDateModal && (
        <ExportOrderData
          setOpenDateModal={setOpenDateModal}
          columns={columns}
        />
      )}
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <OrdersTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
            setSelectedStoreName={setSelectedStoreName}
            orderStatusSelectOptions={orderStatusSelectOptions}
          />
        )}
        {isEditModalOpen && (
          <Edit
            orderId={selectedItemId}
            onCloseHandler={() => setIsEditModalOpen(false)}
          />
        )}
        {isChangeCourierModalOpen && (
          <ChangeOrderCourier
            orderId={selectedItemId}
            couriers={couriersOfZone}
            onCloseHandler={() => {
              setIsChangeCourierModalOpen(false);
              fetchData();
            }}
          />
        )}
        {openOrderTransferModal && (
          <OrderTransfer
            orderNumber={orderNumberForTransfer}
            onSuccessCalback={() => {
              setOpenOrderTransferModal(false);
              fetchData();
            }}
            onCloseHandler={() => {
              setOpenOrderTransferModal(false);
            }}
          />
        )}
      </Suspense>
    </div>
  );
};

export default Orders;
